import { useEffect, useRef } from "react";
import { motion } from "framer-motion";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  type ChartConfiguration,
} from "chart.js";

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

interface Props {
  data: {
    type: "bar_chart";
    title?: string;
    labels: string[];
    values: number[];
    unit?: string;
    color?: string;
    animation?: string;
  };
}

export function ElementBarChart({ data }: Props) {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    if (!canvasRef.current || !data.labels?.length) return;
    const color = data.color || "#6366f1";
    const config: ChartConfiguration<"bar"> = {
      type: "bar",
      data: {
        labels: data.labels,
        datasets: [
          {
            label: data.unit ?? "Value",
            data: data.values,
            backgroundColor: color + "99",
            borderColor: color,
            borderWidth: 1,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          title: { display: !!data.title, text: data.title ?? "" },
          legend: { display: false },
        },
        scales: {
          y: { beginAtZero: true },
        },
      },
    };
    const chart = new ChartJS(canvasRef.current, config);
    return () => chart.destroy();
  }, [data]);

  return (
    <motion.div
      className="h-48 w-full"
      initial={{ opacity: 0, scale: 0.98 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.3 }}
    >
      <canvas ref={canvasRef} />
    </motion.div>
  );
}
